import clsx from 'clsx';
import * as React from 'react';
import scrollIntoViewIfNeeded from 'scroll-into-view-if-needed';

import {
    checkIsTouchDevice,
    type IconDefinition,
    renderIcon,
    useListKeyboardNavigation,
} from '@gitbook/spine-react';

import { AppSearchGroupHeader } from '../../AppSearch/Search/AppSearchGroupHeader';
import { type SearchResultInfo, SearchResultItem } from './SearchResultItem';

export type { SearchResultInfo };

export interface SearchResultGroupInfo {
    suffix?: string;
    icon?: IconDefinition;
    title?: string;
    seperator?: React.ReactNode;
    source?: string;
}

export interface SearchModalResultsProps<Item, Group> {
    /** Items to render in the list. These can be any type of data. */
    data: Item[];
    /** Get a unique key for an item. */
    getKey: (item: Item) => string;
    /** Render an item, this functions returns the metadata used to render the item. */
    renderItem: (item: Item) => SearchResultInfo;
    /** Render info to render for a group */
    renderGroup?: (group: Group) => SearchResultGroupInfo | undefined;
    /** Associate an item with a group. The renderig wil lshow as one pack. */
    groupItem?: (item: Item) => Group;
    /** Get a unique key for a group. */
    getGroupKey?: (item: Group) => string;
    /** Callback when the user is opening an item. */
    onOpen: (item: Item, index: number) => void;
    /** Extra style to be passed to the wrapping view */
    className?: string;
    /** If false, pressing Enter will not open the currently selected item. Should be used when combined to an input being typed in */
    allowEnterToOpen?: boolean;
    /** Query string used to highlight matching text in results */
    query?: string;
}

interface ItemWithGroup<Item, Group> {
    group: Group;
    item: Item;
}

/**
 * Render a list of items with keyboard navigations and smart grouping.
 */
export function SearchModalResults<Item, Group>(
    props: SearchModalResultsProps<Item, Group>
): React.ReactElement {
    const {
        data,
        renderItem,
        renderGroup,
        groupItem,
        getGroupKey,
        getKey,
        onOpen,
        className,
        allowEnterToOpen = true,
        query,
    } = props;

    const elements = React.useRef({});

    const entries = React.useMemo(
        () => groupData(data, groupItem, getGroupKey),
        [data, groupItem, getGroupKey]
    );

    const { cursor, hoverItem, setCursor } = useListKeyboardNavigation({
        active: true,
        startAtFirst: checkIsTouchDevice(),
        items: entries,
        onSelect: allowEnterToOpen
            ? (entry: ItemWithGroup<Item, Group>, info: { index: number }) => {
                  const { index } = info;

                  if (renderItem(entry.item).disabled) {
                      return;
                  }
                  onOpen(entry.item, index);
              }
            : undefined,
        scrollTo: (entry: ItemWithGroup<Item, Group>) => {
            const element = elements.current[getKey(entry.item)];
            if (element) {
                scrollIntoViewIfNeeded(element, {
                    behavior: 'smooth',
                    scrollMode: 'if-needed',
                });
            }
        },
        getKey: (entry) => getKey(entry.item),
    });

    return (
        <ul className={clsx('flex flex-1 flex-col gap-1', className)}>
            {entries.map((entry, index) => {
                const previousEntry = entries[index - 1];
                const previousGroup = previousEntry?.group;

                const key = getKey(entry.item);
                const itemInfo = renderItem(entry.item);

                let header: React.ReactNode | null = null;
                const groupInfo = renderGroup?.(entry.group);

                if (!getGroupKey || getGroupKey(entry.group) !== getGroupKey(previousGroup)) {
                    const previousGroupInfo = renderGroup?.(previousGroup);
                    if (groupInfo) {
                        header = (
                            <>
                                {previousGroupInfo?.seperator ? previousGroupInfo.seperator : null}
                                <div
                                    ref={(ref) => {
                                        elements.current[key] = ref;
                                    }}
                                >
                                    <AppSearchGroupHeader
                                        suffix={groupInfo.suffix}
                                        title={groupInfo.title}
                                        icon={
                                            groupInfo.icon
                                                ? renderIcon(groupInfo.icon, { size: 18 })
                                                : undefined
                                        }
                                    />
                                </div>
                            </>
                        );
                    }
                }

                return (
                    <React.Fragment key={key}>
                        {header}
                        <SearchResultItem
                            ref={
                                header
                                    ? undefined
                                    : (ref) => {
                                          elements.current[key] = ref;
                                      }
                            }
                            {...itemInfo}
                            query={query}
                            active={cursor.current !== null && key === getKey(cursor.current.item)}
                            onHover={() => {
                                hoverItem(entry);
                            }}
                            onFocus={() => {
                                setCursor(entry);
                            }}
                            onSelect={() => {
                                if (itemInfo.disabled) {
                                    return;
                                }
                                onOpen(entry.item, index);
                            }}
                        />
                    </React.Fragment>
                );
            })}
        </ul>
    );
}

function groupData<Item, Group>(
    data: Item[],
    groupItem?: (item: Item) => Group,
    getGroupKey?: (group: Group) => string
): ItemWithGroup<Item, Group>[] {
    if (!groupItem || !getGroupKey) {
        // @ts-ignore
        return data.map((item) => ({ group: undefined, item }));
    }

    const groups: ItemWithGroup<Item, Group>[][] = [];
    data.forEach((item) => {
        const group = groupItem(item);

        const itemsGroup = groups.find(
            (groupArr) => getGroupKey(groupArr[0].group) === getGroupKey(group)
        );
        if (itemsGroup) {
            itemsGroup.push({ item, group });
            return;
        }

        groups.push([
            {
                group,
                item,
            },
        ]);
    });

    return groups.flat();
}
